import { useCallback, useEffect, useMemo } from "react"
import { Routes, Route, useNavigate } from "react-router-dom"
import { AnimatePresence } from "framer-motion"
import { useIdleTimer } from "react-idle-timer"
import _ from "lodash"

import config from "config"
import { DEVICE_KIOSK_H } from "constant"

import {
  set as setPersistValue,
  get as getPersistValue,
} from "utils/persist_store"
import { homeScreenMode, updateAppStatus, APP_STATUS } from "utils/electron"

import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"

import { useSession } from "hooks/analytics/useSession"
import { useUptimeHeartbeat } from "hooks/useUptimeHeartbeat"

// Extensions
import {
  getAllExtensionRoutes,
  getExtensionAppComponentConfig,
  getExtensionAppLayoutConfig,
  getExtensionThemeFactories,
  mergeRoutes,
  registerRouteByConfig,
} from "utils/extension"
import { extensionConfigs } from "extensions"

// Theme
import { generateTheme } from "theme/generateTheme"
import { appThemeOptionFactory as directoryThemeOptionFactory } from "theme/app/directory"

import { ThemeProvider } from "providers/mui-extended/ThemeProvider"

// Routes
import { standardRoutes } from "standard"

// Pages
import { Layout as MapLayout } from "pages/maps/_layout.kiosk-h"

// Components, Layouts
import { AppLayout, PageLayout } from "layouts/kiosk-h"
import { ScrollToTop } from "components/ScrollToTop"
import { MotionDiv, showContent } from "components/motions"
import { LoadingScreen, MapUpdatingScreen } from "components/LoadingScreen"
import { createIndexRoute } from "providers/route/utils/createIndexRoute"
import { MapIndex } from "pages/maps"
import { createTheme } from "@mui/material"
import { HomeScreenSaver } from "components/HomeScreenSaver"

const resetEvent = new Event("appreset")

// Extension routes
const extensionRoutes = getAllExtensionRoutes(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_H
)

// Merge Standard + Extension routes
const { baseRoutes, pageRoutes, mapRoutes } = mergeRoutes(
  _.get(standardRoutes, DEVICE_KIOSK_H),
  extensionRoutes
)

// Extension Themes
const extensionThemeConfig = getExtensionThemeFactories(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_H
)

// Extension App Layout Config
const { search, appNavigation } = getExtensionAppLayoutConfig(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_H
)

// Extension App Components Config
const appComponentConfig = getExtensionAppComponentConfig(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_H
)
const { loadingScreen, preservedView } = appComponentConfig

// App Theme Factory
const appThemeOptionFactory = _.get(directoryThemeOptionFactory, DEVICE_KIOSK_H)

export const KioskHApp = () => {
  // Generate a venue theme
  const {
    themeOptions: venueThemeOptions,
    // refetchImdfData,
    dataLoaded,
    indexPath,
    idleModeConfig,
    screenSaverConfig,
  } = useVenue()
  const { redirectPath, ...useIdleTimerProps } = idleModeConfig

  const {
    INITIAL_SESSION_KEY,
    gtmPushSurveyNewSession,
    gtmPushDataLayerCountKioskUser,
  } = useSession()

  // Periodically ping the uptime monitor so the kiosk is reported as online.
  useUptimeHeartbeat()

  const {
    mapReady,
    mapLoading,
    updateRouteOptions,
    themeDirection,
    isIdleTimerPaused,
  } = useAppUI()
  const theme = generateTheme(venueThemeOptions, appThemeOptionFactory, {
    extendThemeFactories: extensionThemeConfig.themeFactories,
    ignoreAppThemeFactory: extensionThemeConfig.ignoreAppThemeFactory,
    ignoreDeviceThemeFactory: extensionThemeConfig.ignoreDeviceThemeFactory,
  })

  const navigate = useNavigate()
  const isAppReady = useMemo(
    () => mapReady && dataLoaded,
    [mapReady, dataLoaded]
  )
  const onIdle = useCallback(async () => {
    window.dispatchEvent(resetEvent)
    gtmPushSurveyNewSession()
    homeScreenMode()
    updateAppStatus(APP_STATUS.IDLE)
    // navigate last: dispatchEvent runs "appreset" listeners synchronously,
    // so the redirect must come after them to avoid being raced/overridden.
    // e.g. if navigate() ran first:
    //   1. navigate(redirectPath) -> schedules navigation to home
    //   2. dispatchEvent("appreset") -> some listener also calls navigate(x)
    //   3. listener's navigate(x) wins -> kiosk ends up on x, not home
    navigate(redirectPath)
    // Refetch IMDF data on idle
    // await refetchImdfData()
  }, [redirectPath, gtmPushSurveyNewSession, navigate /* , refetchImdfData */])

  const onActive = useCallback(() => {
    gtmPushDataLayerCountKioskUser()
  }, [gtmPushDataLayerCountKioskUser])
  const onAction = useCallback(() => {
    if (!getPersistValue(INITIAL_SESSION_KEY)) {
      gtmPushDataLayerCountKioskUser()
    }
    setPersistValue(INITIAL_SESSION_KEY, true)
  }, [INITIAL_SESSION_KEY, gtmPushDataLayerCountKioskUser])

  useIdleTimer({
    onActive,
    onAction,
    onIdle,
    timeout: 1000 * 60 * 5,
    throttle: 500,
    ...useIdleTimerProps,
    disabled: Boolean(useIdleTimerProps.disabled) || isIdleTimerPaused,
  })

  useEffect(() => {
    updateRouteOptions({ baseRoutes, pageRoutes, mapRoutes })
  }, [updateRouteOptions])

  const themeWithDirection = useMemo(
    () =>
      createTheme(theme, {
        direction: themeDirection,
        isRtl: themeDirection === "rtl",
      }),
    [theme, themeDirection]
  )

  return (
    <ThemeProvider theme={themeWithDirection}>
      <ScrollToTop />
      {/* Todo: preservedView will be removed later use config ScreenSaver (splitr) from theme */}
      {isAppReady ? preservedView : null}
      {/* Add ScreenSaver (Splitr) to standard kiosk-h */}
      {isAppReady && screenSaverConfig.enable ? <HomeScreenSaver /> : null}
      <AnimatePresence mode="sync">
        {!isAppReady
          ? loadingScreen || <LoadingScreen key="loading-screen" />
          : null}
        <MapUpdatingScreen key="map-updating" open={mapLoading} />
        <MotionDiv
          key="content"
          variants={showContent}
          initial="initial"
          animate="animate"
        >
          <Routes>
            <Route
              path="/"
              element={
                <AppLayout search={search} appNavigation={appNavigation} />
              }
            >
              {createIndexRoute({ to: indexPath })}
              {baseRoutes.map(registerRouteByConfig)}
              <Route path="/*" element={<PageLayout />}>
                {/* Add or override specific route extension pages here. */}
                {pageRoutes.map(registerRouteByConfig)}
              </Route>
              <Route path="/maps/*" element={<MapLayout />}>
                <Route index element={<MapIndex />} />
                {/* The indoorPage parent router treats the route child parameter as the same parameter as its own. */}
                {mapRoutes.map(registerRouteByConfig)}
              </Route>
            </Route>
          </Routes>
        </MotionDiv>
      </AnimatePresence>
    </ThemeProvider>
  )
}
